import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CookiesModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CookiesModal({
  isOpen,
  onClose
}: CookiesModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-20">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={onClose} data-id="element-21" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 40
      }} transition={{
        duration: 0.3
      }} className="fixed inset-4 md:inset-10 lg:inset-x-auto lg:top-10 lg:bottom-10 lg:w-[800px] lg:mx-auto bg-navy-900 border border-teal-500/20 rounded-2xl z-50 flex flex-col overflow-hidden" data-id="element-22">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-teal-500/10 flex-shrink-0" data-id="element-23">
              <h2 className="text-xl font-display font-bold text-white" data-id="element-24">
                Política de Cookies
              </h2>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-navy-800 transition-colors text-gray-400 hover:text-white" aria-label="Cerrar" data-id="element-25">
                <X size={20} data-id="element-26" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-27">
              <div className="prose prose-invert max-w-none space-y-8 text-gray-300 leading-relaxed" data-id="element-28">
                <section data-id="element-29">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-30">
                    1. INTRODUCCIÓN
                  </h3>
                  <p data-id="element-31">
                    En TruID, utilizamos cookies y tecnologías similares para
                    mejorar la experiencia de navegación en nuestros sitios web
                    y plataformas, analizar el tráfico, personalizar el
                    contenido y mostrar anuncios relevantes. A continuación, te
                    explicamos qué son las cookies, cómo las usamos y cómo
                    puedes gestionarlas.
                  </p>
                </section>

                <section data-id="element-32">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-33">
                    2. ¿QUÉ SON LAS COOKIES?
                  </h3>
                  <p data-id="element-34">
                    Las cookies son pequeños archivos de texto que un sitio web
                    o plataforma almacena en tu dispositivo (computadora,
                    celular, Tablet u otro) al visitarlo. Estas permiten que el
                    sitio "recuerde" tus acciones o preferencias (como idioma,
                    inicio de sesión, etc.) durante un período de tiempo
                    determinado.
                  </p>
                </section>

                <section data-id="element-35">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-36">
                    3. ¿QUÉ TIPOS DE COOKIES UTILIZAMOS?
                  </h3>
                  <div className="space-y-4" data-id="element-37">
                    <div data-id="element-38">
                      <h4 className="font-semibold text-teal-500 mb-1" data-id="element-39">
                        A. Cookies necesarias (técnicas):
                      </h4>
                      <p data-id="element-40">
                        Permiten la navegación en el sitio web y el uso de sus
                        funciones esenciales. Sin estas cookies, el sitio no
                        puede funcionar correctamente.
                      </p>
                    </div>
                    <div data-id="element-41">
                      <h4 className="font-semibold text-teal-500 mb-1" data-id="element-42">
                        B. Cookies de preferencia o personalización:
                      </h4>
                      <p data-id="element-43">
                        Almacenan información para que el usuario acceda al
                        servicio con características personalizadas, como el
                        idioma o región.
                      </p>
                    </div>
                    <div data-id="element-44">
                      <h4 className="font-semibold text-teal-500 mb-1" data-id="element-45">
                        C. Cookies de análisis o rendimiento:
                      </h4>
                      <p data-id="element-46">
                        Nos permiten conocer el comportamiento de los visitantes
                        para mejorar el sitio. Usamos herramientas como Google
                        Analytics u otras similares.
                      </p>
                    </div>
                    <div data-id="element-47">
                      <h4 className="font-semibold text-teal-500 mb-1" data-id="element-48">
                        D. Cookies de publicidad o marketing:
                      </h4>
                      <p data-id="element-49">
                        Recopilan información sobre tus hábitos de navegación
                        para mostrarte anuncios relevantes en nuestros sitios o
                        en otros.
                      </p>
                    </div>
                  </div>
                </section>

                <section data-id="element-50">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-51">
                    4. ¿CUÁNTO TIEMPO PERMANECEN ACTIVAS?
                  </h3>
                  <ul className="list-disc list-inside space-y-2" data-id="element-52">
                    <li data-id="element-53">
                      <span className="text-white font-medium" data-id="element-54">
                        Cookies de sesión:
                      </span>{' '}
                      se eliminan al cerrar el navegador.
                    </li>
                    <li data-id="element-55">
                      <span className="text-white font-medium" data-id="element-56">
                        Cookies persistentes:
                      </span>{' '}
                      permanecen en tu dispositivo hasta que las elimines o
                      expiren.
                    </li>
                  </ul>
                </section>

                <section data-id="element-57">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-58">
                    5. ¿CÓMO PUEDES CONTROLAR LAS COOKIES?
                  </h3>
                  <p className="mb-3" data-id="element-59">
                    Al ingresar por primera vez a nuestro sitio, se te mostrará
                    un aviso de cookies. Puedes aceptar todas, rechazarlas o
                    configurarlas según tu preferencia.
                  </p>
                  <p className="mb-3" data-id="element-60">
                    Además, puedes modificar la configuración de cookies desde
                    tu navegador en cualquier momento:
                  </p>
                  <ul className="list-disc list-inside space-y-2" data-id="element-61">
                    <li data-id="element-62">
                      <span className="text-white font-medium" data-id="element-63">
                        Google Chrome:
                      </span>{' '}
                      Configuración de cookies
                    </li>
                    <li data-id="element-64">
                      <span className="text-white font-medium" data-id="element-65">
                        Mozilla Firefox:
                      </span>{' '}
                      Administrar cookies
                    </li>
                    <li data-id="element-66">
                      <span className="text-white font-medium" data-id="element-67">Safari:</span>{' '}
                      Soporte de Safari
                    </li>
                    <li data-id="element-68">
                      <span className="text-white font-medium" data-id="element-69">
                        Microsoft Edge:
                      </span>{' '}
                      Privacidad y servicios
                    </li>
                  </ul>
                </section>

                <section data-id="element-70">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-71">
                    6. COOKIES DE TERCEROS
                  </h3>
                  <p className="mb-3" data-id="element-72">
                    Algunas cookies pueden ser instaladas por servicios de
                    terceros utilizados en nuestro sitio (como redes sociales,
                    plataformas de video, servicios de estadísticas, etc.). No
                    tenemos control directo sobre ellas. Te recomendamos revisar
                    sus respectivas políticas:
                  </p>
                  <ul className="list-disc list-inside space-y-2" data-id="element-73">
                    <li data-id="element-74">
                      <span className="text-white font-medium" data-id="element-75">Google:</span>{' '}
                      Política de privacidad
                    </li>
                    <li data-id="element-76">
                      <span className="text-white font-medium" data-id="element-77">
                        Meta (Facebook / Instagram):
                      </span>{' '}
                      Política de cookies
                    </li>
                  </ul>
                </section>

                <section data-id="element-78">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-79">
                    7. CAMBIOS EN ESTA POLÍTICA
                  </h3>
                  <p data-id="element-80">
                    Podemos actualizar esta Política de Cookies en cualquier
                    momento. Te notificaremos en nuestro sitio web cuando se
                    hagan cambios relevantes.
                  </p>
                </section>

                <section data-id="element-81">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-82">
                    8. CONTACTO
                  </h3>
                  <p data-id="element-83">
                    Si tienes dudas sobre nuestra política de cookies, puedes
                    escribirnos a:
                  </p>
                  <p className="mt-2" data-id="element-84">
                    <a href="mailto:protecciondatos@truid.org" className="text-teal-500 hover:text-teal-400 transition-colors font-medium" data-id="element-85">
                      protecciondatos@truid.org
                    </a>
                  </p>
                </section>

                <section data-id="element-86">
                  <h3 className="text-lg font-display font-semibold text-white mb-3" data-id="element-87">
                    9. VIGENCIA
                  </h3>
                  <p data-id="element-88">
                    La vigencia de estas Políticas es desde el 21 de noviembre
                    de 2025 y permanecerá vigente hasta que exista una nueva
                    versión que la reemplace.
                  </p>
                </section>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}